import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { DeleteOutlined, FileUploadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { useSelfServeAggregatedCohortDataFileUploadMutation } from '@api/selfServe';
interface FileDetails {
  filePath: string;
  fileType: string;
  fileName: string;
}
interface AggregatedCohortDataDetailsProps {
  selfServeId: string;
  formValues: Record<string, string | string[]>;
  handleInputChange: (name: string, value: string | string[]) => void;
  setIsFormValid?: (isValid: boolean) => void;
}

// const AGGREGATED_COHORT_DATA_PUBLIC_URL_REGEX =
//   /^https:\/\/docs\.google\.com\/spreadsheets\/(?:u\/\d+\/)?d\/[a-zA-Z0-9-_]+(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/;

const sampleDocumentUrl = `${process.env.REACT_APP_BUCKET_DOMAIN}/demo-documents/Aggregated%20Cohort%20Data%20for%20PvX%20Capital%20-%20Template.xlsx`;

const AggregatedCohortDataDetails: React.FC<AggregatedCohortDataDetailsProps> = ({
  selfServeId,
  formValues,
  handleInputChange,
  setIsFormValid,
}) => {
  const connectionDetails = JSON.parse((formValues.connectionDetails as string) || '{}');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [uploadAggregatedCohortDataFile] = useSelfServeAggregatedCohortDataFileUploadMutation();

  const [fileDetails, setFileDetails] = useState<FileDetails | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isTouched, setIsTouched] = useState({
    publicUrlField: false,
  });

  useEffect(() => {
    const isPublicUrlValid = !!connectionDetails?.publicUrl;
    const isFileUploaded = Boolean(connectionDetails?.filePath && connectionDetails?.fileType);

    setIsFormValid?.(isPublicUrlValid || isFileUploaded);
  }, [connectionDetails]);

  useEffect(() => {
    if (fileDetails) {
      setIsTouched((prev) => ({
        ...prev,
        publicUrlField: false,
      }));
    }
  }, [fileDetails]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const validTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'text/csv',
    ];
    const isValidType = validTypes.includes(file?.type || '');
    if (!isValidType) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: `File "${file?.name}" is not a supported format.`,
        }),
      );
      return;
    }
    if (file && isValidType) {
      uploadAggregatedCohortDataFile({ selfServeId, file })
        .unwrap()
        .then((res) => {
          setFileDetails({
            filePath: res?.data?.filePath,
            fileType: res?.data?.fileType,
            fileName: file?.name,
          });
          handleInputChange(
            'connectionDetails',
            JSON.stringify({
              ...connectionDetails,
              type: 'FILE_UPLOAD',
              fileType: res?.data?.fileType,
              filePath: res?.data?.filePath,
              publicUrl: undefined,
            }),
          );
        });
    }
  };

  const handleFileDelete = () => {
    setFileDetails(null);
    handleInputChange(
      'connectionDetails',
      JSON.stringify({
        ...connectionDetails,
        type: undefined,
        fileType: undefined,
        filePath: undefined,
        publicUrl: undefined,
      }),
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '4rem',
        width: '100%',
      }}
    >
      <Box
        sx={{
          flex: 1,
          py: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: '1.375rem',
            color: '#162936',
          }}
        >
          Create a cohort view with cumulative NET ROAS by each elapsed month and upload either as a
          CSV or a Google Sheet.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#162936',
              letterSpacing: '-0.35px',
            }}
          >
            1. Download our Sample Spreadsheet
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexDirection: 'row',
              backgroundColor: '#F0F0F1',
              border: '1px solid #2F736E1F',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              width: 'fit-content',
            }}
            onClick={() => window.open(sampleDocumentUrl, '_blank')}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: '1rem',
                color: '#162936',
              }}
            >
              Sample Sheet
            </Typography>
            <SimCardDownloadOutlinedIcon sx={{ color: '#51B8B0' }} />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#162936',
              letterSpacing: '-0.35px',
            }}
          >
            2. Upload your Cohort file in the above format ( .xls, .xlsx, .csv only)
          </Typography>
          {fileDetails ? (
            <Box
              key={fileDetails?.fileName}
              sx={{
                backgroundColor: '#F0F0F1',
                padding: '1rem 0.5rem',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                width: '40%',
                height: '3rem',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '85%',
                }}
              >
                <InsertDriveFileOutlined sx={{ color: '#2F736E' }} />
                <Typography
                  sx={{
                    color: '#6C6C6C',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    overflowX: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fileDetails?.fileName}
                </Typography>
              </Box>
              <DeleteOutlined
                sx={{ color: '#2F736E', cursor: 'pointer' }}
                onClick={handleFileDelete}
              />
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                padding: '0.75rem 1.5rem',
                textTransform: 'none',
                width: 'fit-content',
                border: '1px solid #2E4A4F',
                borderRadius: '100px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                letterSpacing: '-0.35px',
                color: '#6C6C6C',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'white',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  opacity: 0.5,
                  backgroundColor: 'white',
                },
              }}
              disabled={!!connectionDetails?.publicUrl}
            >
              <FileUploadOutlined
                sx={{
                  height: '1.125rem',
                  width: '1.125rem',
                  color: '#2E4A4F',
                }}
              />
              Upload File
              <input
                type="file"
                accept=".xls, .xlsx, .csv"
                multiple
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Button>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              color: '#162936',
              letterSpacing: '-0.35px',
              opacity: !!fileDetails || !!connectionDetails?.publicUrl ? 0.5 : 1,
            }}
          >
            Or
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <TextField
              label="Add Google Sheet Link"
              placeholder="Add Public Link here"
              sx={{
                width: isMobile ? '100%' : '50%',
                opacity: !!fileDetails ? 0.5 : 1,
              }}
              value={connectionDetails?.publicUrl || ''}
              onChange={(e) =>
                handleInputChange(
                  'connectionDetails',
                  JSON.stringify({
                    ...connectionDetails,
                    type: 'PUBLIC_URL',
                    fileType: undefined,
                    filePath: undefined,
                    publicUrl: e.target.value,
                  }),
                )
              }
              error={!connectionDetails?.publicUrl && isTouched.publicUrlField}
              onBlur={(e) => {
                setIsTouched((prev) => ({
                  ...prev,
                  publicUrlField: true,
                }));
              }}
              disabled={!!fileDetails}
            />
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                color: '#6C6C6C',
              }}
            >
              Please provide comment access to anyone with the link.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AggregatedCohortDataDetails;
